import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import {
  Button,
  OpenLink,
  PlusIcon,
  Search,
  Selector,
  SortableTableHeader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui";
import {
  Empty,
  ErrorState,
  Loading,
  PageHeader,
} from "../../components/WorkspacePrimitives";
import {
  useAgentCompanies,
  useAgentModels,
  type AgentCompany,
  type AgentModel,
} from "../api/e2e";
import CreateCompanyModal from "../components/CreateCompanyModal";
import { relativeTime } from "../lib/format";
import { formatCountry } from "../lib/companyCatalog";

type CompanyReleaseFilter = "all" | "released" | "unreleased";
type CompanySort = "name_asc" | "name_desc" | "release_desc" | "release_asc";

const COMPANY_SORTS: Record<
  CompanySort,
  { sortBy: "name" | "release_date"; sortOrder: "asc" | "desc" }
> = {
  name_asc: { sortBy: "name", sortOrder: "asc" },
  name_desc: { sortBy: "name", sortOrder: "desc" },
  release_desc: { sortBy: "release_date", sortOrder: "desc" },
  release_asc: { sortBy: "release_date", sortOrder: "asc" },
};

function CompanyRow({
  company,
  latest,
}: {
  company: AgentCompany;
  latest?: AgentModel;
}) {
  return (
    <TableRow className="company-catalog__row">
      <TableCell>
        <div className="company-catalog__company">
          <span className="eyebrow">{company.ticker}</span>
          <Link to={`/companies/${encodeURIComponent(company.id)}`}>
            <strong>{company.company_name}</strong>
          </Link>
        </div>
      </TableCell>
      <TableCell>{formatCountry(company.country)}</TableCell>
      <TableCell>
        {latest
          ? `${latest.sequence} model${latest.sequence === 1 ? "" : "s"}`
          : "0 models"}
      </TableCell>
      <TableCell>
        <div className="company-catalog__latest">
          {latest ? (
            <>
              <strong>Version {latest.sequence}</strong>
              <small>Published {relativeTime(latest.published_at)}</small>
            </>
          ) : (
            <small>No model published</small>
          )}
        </div>
      </TableCell>
      <TableCell className="company-catalog__open">
        <OpenLink
          to={`/companies/${encodeURIComponent(company.id)}`}
          label={`Open ${company.company_name}`}
        />
      </TableCell>
    </TableRow>
  );
}

export default function AgentCompaniesPage() {
  const [search, setSearch] = useState("");
  const [country, setCountry] = useState("");
  const [releaseFilter, setReleaseFilter] =
    useState<CompanyReleaseFilter>("all");
  const [sort, setSort] = useState<CompanySort>("name_asc");
  const selectedSort = COMPANY_SORTS[sort];
  const nameDirection =
    selectedSort.sortBy === "name" ? selectedSort.sortOrder : null;
  const releaseDirection =
    selectedSort.sortBy === "release_date" ? selectedSort.sortOrder : null;
  const allCompanies = useAgentCompanies();
  const companies = useAgentCompanies({
    search,
    country,
    hasModel:
      releaseFilter === "all" ? undefined : releaseFilter === "released",
    ...selectedSort,
  });
  const models = useAgentModels();
  const [creatingCompany, setCreatingCompany] = useState(false);
  const latestByCompany = useMemo(
    () =>
      new Map((models.data ?? []).map((model) => [model.company_id, model])),
    [models.data],
  );
  const countryOptions = useMemo(
    () => [
      { value: "", label: "All countries" },
      ...Array.from(
        new Set((allCompanies.data ?? []).map((company) => company.country)),
      )
        .sort((left, right) =>
          formatCountry(left).localeCompare(formatCountry(right)),
        )
        .map((value) => ({ value, label: formatCountry(value) })),
    ],
    [allCompanies.data],
  );
  const hasFilters =
    search.trim() !== "" || country !== "" || releaseFilter !== "all";

  if (
    (companies.isPending && !companies.data) ||
    (allCompanies.isPending && !allCompanies.data) ||
    (models.isPending && !models.data)
  ) {
    return <Loading label="Loading companies…" />;
  }
  if (
    (companies.error && !companies.data) ||
    (allCompanies.error && !allCompanies.data)
  ) {
    return (
      <ErrorState
        error={
          companies.error ??
          allCompanies.error ??
          new Error("Company catalog unavailable")
        }
        onRetry={() =>
          void Promise.all([companies.refetch(), allCompanies.refetch()])
        }
      />
    );
  }
  if (models.error && !models.data) {
    return <ErrorState error={models.error} onRetry={() => models.refetch()} />;
  }

  return (
    <div className="page page--workspace page--fixed-catalog-header">
      <PageHeader
        className="company-catalog-header"
        aria-label="Company catalog controls"
      >
        <h1>Companies</h1>
        <Search
          value={search}
          onValueChange={setSearch}
          placeholder="Search company or ticker"
          aria-label="Search companies"
        />
        <Selector
          options={countryOptions}
          value={country}
          onValueChange={setCountry}
          ariaLabel="Filter companies by country"
          searchable
        />
        <Selector
          options={[
            { value: "all", label: "All publication states" },
            { value: "released", label: "Published models" },
            { value: "unreleased", label: "No published models" },
          ]}
          value={releaseFilter}
          onValueChange={setReleaseFilter}
          ariaLabel="Filter companies by publication state"
        />
        <Button
          tone="primary"
          aria-label="Create company"
          iconLeft={<PlusIcon />}
          onClick={() => setCreatingCompany(true)}
        >
          Create company
        </Button>
      </PageHeader>

      <div className="fixed-catalog-scroll">
        {!allCompanies.data || allCompanies.data.length === 0 ? (
          <Empty>No companies have been created yet.</Empty>
        ) : !companies.data || companies.data.length === 0 ? (
          <Empty>
            {hasFilters
              ? "No companies match these filters."
              : "No companies are available."}
          </Empty>
        ) : (
          <div className="company-table-wrap">
            <Table className="company-catalog" aria-busy={companies.isFetching}>
              <TableHead>
                <TableRow>
                  <SortableTableHeader
                    direction={nameDirection}
                    onSort={() =>
                      setSort(sort === "name_asc" ? "name_desc" : "name_asc")
                    }
                  >
                    Company
                  </SortableTableHeader>
                  <TableHeader>Country</TableHeader>
                  <TableHeader>Models</TableHeader>
                  <SortableTableHeader
                    direction={releaseDirection}
                    onSort={() =>
                      setSort(
                        sort === "release_desc"
                          ? "release_asc"
                          : "release_desc",
                      )
                    }
                  >
                    Latest model
                  </SortableTableHeader>
                  <TableHeader aria-label="Open" />
                </TableRow>
              </TableHead>
              <TableBody>
                {companies.data.map((company) => (
                  <CompanyRow
                    key={company.id}
                    company={company}
                    latest={latestByCompany.get(company.id)}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
      {creatingCompany && (
        <CreateCompanyModal onClose={() => setCreatingCompany(false)} />
      )}
    </div>
  );
}
